import type { FilterKey } from "stores/ApplicationFilters/ApplicationFilterItemsStore";

/**
 * Static filter keys supported by object-level filtering.
 */
const ALL_OBJECT_FILTER_KEYS = [
    "agentIds",
    "agentManagerIds",
    "mediaTypes",
    "callDirections",
    "callDuration",
    "messageCount",
    "evaluationTypes",
    "hierarchyIds",
    "clientCallId",
    "languages",
    "meta1",
    "meta2",
    "meta3",
    "meta4",
    "meta5",
    "favoriteConversations",
    "embeddedTranscriptionQuery",
    "wordsAndPhrasesSearchString",
    "eddyEffectStatus",
    "beginningSentiment",
    "endingSentiment",
    "rbcFilterItems",
    "dataLabels",
    "topics",
    "themes",
    "primaryTopics",
    "drivers",
    "contactTypes",
    "patientJourneyStages",
    "safetyEvent",
    "hipaaCompliance",
    "modelResults",
    "outlineSegmentFilterItems",
    "moduleScores",
    "questionResults",
] as const satisfies readonly FilterKey[];

export type StaticObjectFilterKey = (typeof ALL_OBJECT_FILTER_KEYS)[number];
export type ExtendedMetadataObjectFilterKey = `extendedMetadata.${string}`;
export type ObjectFilterKey = StaticObjectFilterKey | ExtendedMetadataObjectFilterKey;

/**
 * Use this list to explicitly disable object-level filters.
 * Empty means all supported static keys are enabled.
 */
export const OBJECT_FILTER_DISABLED_KEYS = [] as const satisfies readonly StaticObjectFilterKey[];

const OBJECT_FILTER_DISABLED_KEY_SET = new Set<string>(
    OBJECT_FILTER_DISABLED_KEYS,
);

export const OBJECT_FILTER_KEYS: readonly StaticObjectFilterKey[] =
    ALL_OBJECT_FILTER_KEYS.filter(
        (key) => !OBJECT_FILTER_DISABLED_KEY_SET.has(key),
    );

export const OBJECT_DATE_TYPE_FILTER_KEY = "dateReferenceOption" as const;
export const OBJECT_CALL_DURATION_FILTER_KEY = "callDuration" as const;
export const OBJECT_MESSAGE_COUNT_FILTER_KEY = "messageCount" as const;

export const OBJECT_SELECTABLE_FILTER_KEYS = [
    ...OBJECT_FILTER_KEYS,
    OBJECT_DATE_TYPE_FILTER_KEY,
] as const satisfies readonly FilterKey[];

export type ObjectSelectableFilterKey =
    | ObjectFilterKey
    | typeof OBJECT_DATE_TYPE_FILTER_KEY;

export const OBJECT_DATE_FILTER_KEY = "dateRange" as const;

type ExtendedMetadataFilterValues = {
    extendedMetadata?: Record<string, string>;
    extendedMetadataGrouped?: Record<string, { values?: string[] }>;
};

/** Returns true when a value is a non-empty trimmed string. */
function isNonEmptyString(value: unknown): value is string {
    return typeof value === "string" && value.trim().length > 0;
}

/** Returns true when grouped metadata contains at least one non-empty value. */
function hasGroupedValues(entry: { values?: string[] } | undefined): boolean {
    return (
        !!entry &&
        Array.isArray(entry.values) &&
        entry.values.some((value) => isNonEmptyString(value))
    );
}

/** Type guard for dynamic `extendedMetadata.*` object filter keys. */
export function isExtendedMetadataObjectFilterKey(
    key: string,
): key is ExtendedMetadataObjectFilterKey {
    return key.startsWith("extendedMetadata.");
}

/** Type guard for statically-declared object filter keys. */
export function isStaticObjectFilterKey(
    key: string,
): key is StaticObjectFilterKey {
    return OBJECT_FILTER_KEYS.includes(key as StaticObjectFilterKey);
}

/** Returns dynamic metadata keys that are currently set on the provided filters object. */
export function getExtendedMetadataObjectFilterKeysFromFilters(
    filters?: ExtendedMetadataFilterValues | null,
): ExtendedMetadataObjectFilterKey[] {
    if (!filters) return [];

    const keys = new Set<ExtendedMetadataObjectFilterKey>();

    Object.entries(filters.extendedMetadata ?? {}).forEach(
        ([metadataKey, value]) => {
            if (!metadataKey || !isNonEmptyString(value)) return;
            keys.add(`extendedMetadata.${metadataKey}`);
        },
    );

    Object.entries(filters.extendedMetadataGrouped ?? {}).forEach(
        ([metadataKey, value]) => {
            if (!metadataKey || !hasGroupedValues(value)) return;
            keys.add(`extendedMetadata.${metadataKey}`);
        },
    );

    return Array.from(keys).sort((left, right) => left.localeCompare(right));
}

/** Returns all currently supported object-filter keys for the provided filter state. */
export function getObjectFilterKeysFromFilters(
    filters?: ExtendedMetadataFilterValues | null,
): ObjectFilterKey[] {
    return [
        ...OBJECT_FILTER_KEYS,
        ...getExtendedMetadataObjectFilterKeysFromFilters(filters),
    ];
}
